import { format, isValid } from 'date-fns';
import { graphql } from 'gatsby';
import { get, kebabCase, startCase, orderBy, sortBy } from 'lodash';
import React, { Component, Fragment } from 'react';
import { STATES } from  '@upsolve/shared';
import { A, H1, H5, H6 } from '@upsolve/ui';

import municipalSearchPlaces from '../../content/onBuild/municipalSearchPlaces';

import { Ampify } from '../components/AMP/AmpContext';
import TopLevelLayout from '../components/Layout/TopLevelLayout';
import SEO from '../components/SEO/SEO';
import MainHeader from '../components/Layout/Header'
import MainFooter from '../components/Layout/Footer'
import CTASideBySideOfferings from '../components/CTA/CTASideBySideOfferings';
import Article from '../components/Layout/Article.div';
import VideoInContext from '../components/Media/VideoInContext';
import HorizontalRule from '../components/Layout/HorizontalRule.hr'
import ContentAccordian from '../components/Layout/ContentAccordian';
import getHeadersFromRichText from '../components/Contentful/getHeadersFromRichText';
import richTextNodeToComponent from '../components/Contentful/richTextNodeToComponent';
import richTextNodesInSections from '../components/Contentful/richTextNodesInSections';
import checkForInlineCta from '../components/Contentful/checkForInlineCta';
import ContentShareRow from '../components/Share/ContentShareRow';
import * as PAGE_TYPES from '../components/Programmatic/pageTypes';
import ImageInContext from '../components/Media/ImageInContext';
import CTATestimonial from '../components/Programmatic/CTATestimonial';
import TestimonialsPreview from '../components/Programmatic/TestimonialsPreview';
import trackPageView from '../components/analytics/trackPageView';
import * as CONTENT_GROUPS from '../components/analytics/contentGroups';
import * as CONTENT_CREATION_METHODS from '../components/analytics/contentCreationMethods';
import SidebarCta from '../components/CTA/SidebarCta';
import SidebarAboutUs from '../components/Layout/SidebarAboutUs';
import HeroFilerCta from '../components/CTA/HeroFilerCta';
import HeroLeadCta from '../components/CTA/HeroLeadCta';
import UpsolveFilersCount from '../components/ProofBuilders/UpsolveFilersCount';
import BreadcrumbNav from '../components/Layout/BreadcrumbNav';
import getWordCountFromRichText from '../components/Contentful/getWordCountFromRichText';
import AuthorsBlock from '../components/Layout/AuthorsBlock';

export default class PageTemplate extends Component {
  componentDidMount() {
    const pageViewProps = {
      contentCreationMethod: get(this.props, 'data.page.contentTraits.creationMethod', CONTENT_CREATION_METHODS.WRITTEN),
      contentGroup: get(this.props, 'data.page.contentTraits.contentGroup'),
      contentLocalized: get(this.props, 'data.page.contentTraits.contentLocalized')
        || get(this.props, 'data.page.locality') != null,
      contentTopics: get(this.props, 'data.page.contentTraits.contentTopics', []),
      searchTermTarget: get(this.props, 'data.page.contentTraits.searchTermTarget'),
      wordCount: getWordCountFromRichText(get(this.props, 'data.page.content.json', [])),
    };
    if (pageViewProps.contentGroup == null) {
      const pageType = get(this.props, 'data.page.type');
      if (pageType === PAGE_TYPES.BARE) {
        pageViewProps.contentGroup = CONTENT_GROUPS.LANDING_PAGE;
      }
      if (pageType === PAGE_TYPES.LEARN_ARTICLE) {
        pageViewProps.contentGroup = CONTENT_GROUPS.LEARN_ARTICLE;
      }
      if ([PAGE_TYPES.STATE_PAGE, PAGE_TYPES.STATE_SUB_PAGE,
        PAGE_TYPES.STATE_BANKRUPTCY_FORMS, PAGE_TYPES.STATE_MEANS_TEST].includes(pageType)) {
        pageViewProps.contentGroup = CONTENT_GROUPS.STATE_PAGE;
      }
      if ([PAGE_TYPES.CITY_PAGE, PAGE_TYPES.CITY_SUB_PAGE,
        PAGE_TYPES.CITY_ATTORNEY_PRICING].includes(pageType)) {
        pageViewProps.contentGroup = CONTENT_GROUPS.CITY_PAGE;
      }
    }
    trackPageView(pageViewProps);
  }

  render() {
    const { path } = this.props;
    const {
      type,
      title,
      seo,
      locality,
      createdAt,
      updatedAt,
      summary,
      content,
      video,
    } = this.props.data.page;
    const { amp } = this.props.pageContext;
    const layout = this.props.data.page.layout || {};
    const relatedSearchPlace = type === PAGE_TYPES.CITY_PAGE
      ? municipalSearchPlaces.find((sp) => ['city', 'town'].includes(sp.type)
        && sp.state.toLowerCase() === get(locality, 'state', '').toLowerCase()
        && sp.nameSlug.toLowerCase() === get(locality, 'city', '').toLowerCase())
      : null;

    // Setup - Associated Page Data
    const clusterChildren = this.props.data.childrenPages.edges.map(e => e.node)
      .filter(n => !n.path.includes('/questions/'));
    const clusterSiblings = this.props.data.siblingPages.edges.map(e => e.node)
      .filter(n => !n.path.includes('/questions/'));

    // Setup - Content
    const sectionHeaders = getHeadersFromRichText(content.json);
    const contentSections = richTextNodesInSections(content.json);
    const hasInlineCta = checkForInlineCta(content.json);
    const authors = (get(this.props, 'data.page.authors') || [get(this.props, 'data.page.author')])
      .filter((author) => author && author.name != null);

    const children = (
      <TopLevelLayout
        showPersistentCta={layout.showPersistentCta}
        persistentCtaText={layout.persistentCtaText}
        showBackground={layout.showBackground}
        backgroundTemplate={layout.backgroundTemplate}
      >
        <SEO
          seoNode={seo}
          pageNode={this.props.data.page}
        />
        <MainHeader
          showHelloBar={layout.showHelloBar}
          helloBarTemplate={layout.helloBarTemplate}
          helloBarText={layout.helloBarText}
          helloBarUrl={layout.helloBarUrl}
        />
        <Article>
          <BreadcrumbNav />
          <div className="article__body">
            <article className="article__content">
              <div className="content-hero">
                <header className="content-hero__header" id="top">
                  <H1>{title}</H1>
                  {type !== PAGE_TYPES.BARE && (
                    <Fragment>
                      <UpsolveFilersCount />
                      <HorizontalRule />
                      <AuthorsBlock
                        authors={authors}
                        createdAt={createdAt}
                        updatedAt={updatedAt}
                      />
                    </Fragment>
                  )}
                </header>
                {layout.showHeroCta !== false && (
                  <div className="content-hero__cta">
                    {(layout.heroCtaType === 'filer' || layout.heroCtaType == null) && <HeroFilerCta />}
                    {layout.heroCtaType === 'lead' && <HeroLeadCta />}
                  </div>
                )}
              </div>
              <div className={type === PAGE_TYPES.BARE ? `content-body full-width` : `content-body`}>
                {get(summary, 'json') != null && (
                  <div className="content-body__lead">
                    <div className="summary">
                      <b>Summary</b>
                      {Array.prototype.map.call(summary.json.content, (c, i, a) => richTextNodeToComponent(c, i, a, { locality }))}
                    </div>
                  </div>
                )}
                {sectionHeaders.length > 0 && (
                  <nav>
                    <H6>Read on to learn more about:</H6>
                    <ul className="content-body__nav">
                      {Array.prototype.map.call(sectionHeaders, (header, headerIndex) => (
                        <li key={headerIndex}>
                          <a aria-busy="false" href={`#${kebabCase(header)}`}>{header}</a>
                        </li>
                      ))}
                    </ul>
                  </nav>
                )}
                {video != null && (
                  <section className="content-video">
                    <VideoInContext video={video} />
                  </section>
                )}
                {contentSections.length === 1
                  ? Array.prototype.map.call(contentSections[0], (c, i, a) => richTextNodeToComponent(c, i, a, { locality }))
                  : Array.prototype.map.call(contentSections, (section, sectionIndex, sectionArray) => (
                    <Fragment key={sectionIndex}>
                      <section>
                        {Array.prototype.map.call(section, (c, i, a) => richTextNodeToComponent(c, i, a, { locality }))}
                        {(sectionIndex !== 0 && section.length > 1)
                          && <small className="back-to-top"><a href="#top">↑ Back to top</a></small>}
                      </section>
                      {(hasInlineCta === false && Math.floor(sectionArray.length * 0.35) === sectionIndex) &&
                        <CTATestimonial />}
                      {(hasInlineCta === false && Math.floor(sectionArray.length * 0.7) === sectionIndex) &&
                        <TestimonialsPreview />}
                    </Fragment>
                  ))}
              </div>
            </article>
            {type !== PAGE_TYPES.BARE && (
              <aside className="article__sidebar">
                {type !== PAGE_TYPES.JOB_LISTING && <SidebarAboutUs />}
                {type !== PAGE_TYPES.JOB_LISTING && <SidebarCta />}
                {type !== PAGE_TYPES.JOB_LISTING && (clusterSiblings.length > 0 || clusterChildren.length > 0) && (
                  <section>
                    <H5>Related Reading</H5>
                    <ul>
                      {Array.prototype.map.call(sortBy([...clusterChildren, ...clusterSiblings]), (c, cIndex) => (
                        <li key={cIndex}>
                          <a href={c.path}>
                            {get(c, 'context.title') || `${STATES[c.path.slice(1,2)] || ''} ${startCase(c.path.slice(3).replace(/\//g, ' '))}`.trim()}
                          </a>
                        </li>
                      ))}
                      {(type === PAGE_TYPES.CITY_PAGE && Number(get(relatedSearchPlace, 'attorneyCount', 0)) >= Number(MINIMUM_SEARCH_PLACE_ATTORNEY_COUNT)) && (
                        <li>
                          <a href={`/bankruptcy-attorneys/${relatedSearchPlace.formattedSlug}`}>
                            {relatedSearchPlace.formattedAddress} Attorneys
                          </a>
                        </li>
                      )}
                    </ul>
                  </section>
                )}
                {type === PAGE_TYPES.STATE_PAGE && (
                  <Fragment>
                    <section>
                      <H5>City Guides</H5>
                      <ContentAccordian height="320px">
                        <ul>
                          {orderBy(municipalSearchPlaces.filter(c => ['city', 'town'].includes(c.type) && new RegExp(c.state, 'i').test(locality.state)), ['population'], ['desc'])
                            .map((city, cityIndex) => (
                              <li key={cityIndex}>
                                <a href={`/${locality.state}/${city.city.replace(/\s/g, '-')}/`.toLowerCase()}>
                                  {city.city}
                                </a>
                              </li>
                            ))}
                        </ul>
                      </ContentAccordian>
                    </section>
                  </Fragment>
                )}
                {type === PAGE_TYPES.JOB_LISTING && (
                  <section>
                    <H6>🗽 Made in New York City</H6>
                    <br />
                    <iframe width="100%" height="240" frameborder="0" style={({ border: '0' })} src="https://www.google.com/maps/embed/v1/place?key=AIzaSyCZ8I8Zyr9Ex3CxhuGEcsHSrIKrBIwURXk&amp;q=Manhattan, NY, United States"></iframe>
                  </section>
                )}
                <ContentShareRow text={title} slug={path} />
              </aside>
            )}
          </div>
        </Article>
        {![PAGE_TYPES.BARE, PAGE_TYPES.JOB_LISTING].includes(type) && <CTASideBySideOfferings />}
        <MainFooter />
      </TopLevelLayout>
    );

    return amp === true
      ? <Ampify>{children}</Ampify>
      : children;
  }
}

// TODO: Move SEO fragment to a StaticQuery within SEO.jsx
// query PageByPath($path: String!, $childrenPathRegex: String, $siblingPathRegex: String) {
export const pageQuery = graphql`
  query PageByPath($canonicalPath: String!,
    $childrenPathRegex: String, $excludedChildrenPageTypes: [String], $excludedChildrenTemplates: String
    $siblingPathRegex: String, $excludedSiblingPageTypes: [String], $excludedSiblingTemplates: String
  ) {
    page: contentfulPage(path: { eq: $canonicalPath }) {
      path
      title
      type
      content {
        json
      }
      summary {
        json
      }
      author {
        ...AuthorNode
      }
      createdAt
      updatedAt
      locality {
        level
        state
        city
      }
      contentTraits {
        contentCreationMethod
        contentGroup
        contentLocalized
        contentTopics
        searchTermTarget
      }
      layout {
        showHelloBar
        showPersistentCta
        showHeroCta
        showBackground
        helloBarTemplate
        helloBarText
        helloBarUrl
        persistentCtaText
        heroCtaType
        backgroundTemplate
      }
      video {
        ...VideoNode
      }
    }

    childrenPages: allSitePage(
      filter: {
        path: {
          regex: $childrenPathRegex
          ne: $canonicalPath
        }
        component: {
          regex: $excludedChildrenTemplates
        }
        context: {
          amp: { ne: true }
          noIndex: { ne: true }
          type: { nin: $excludedChildrenPageTypes }
        }
      }
    ) {
      edges {
        node {
          path
          component
          context {
            title
          }
        }
      }
    }

    siblingPages: allSitePage(
      filter: {
        path: {
          regex: $siblingPathRegex
          ne: $canonicalPath
        },
        component: {
          regex: $excludedSiblingTemplates
        }
        context: {
          amp: { ne: true }
          noIndex: { ne: true }
          type: { nin: $excludedSiblingPageTypes }
        }
      }
    ) {
      edges {
        node {
          path
          component
          context {
            title
          }
        }
      }
    }
  }
`;
